const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const adminBookingService = {
  // Patient search methods
  searchPatientById: async (patientId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/patients/by-patient-id/${patientId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  searchPatientByNameAndDOB: async (firstName, lastName, dob) => {
    const token = localStorage.getItem('authToken');
    
    console.log('🔍 Searching for patient:', { firstName, lastName, dob });

    // Try different search approaches
    const searchTerms = [
      `${firstName} ${lastName}`.trim(),
      firstName.trim(),
      lastName.trim(),
      `${lastName} ${firstName}`.trim(), // Sometimes names are reversed
    ];

    let foundPatients = [];

    // Try each search term
    for (const searchTerm of searchTerms) {
      if (!searchTerm) continue;
      
      try {
        console.log(`🔍 Trying search term: "${searchTerm}"`);
        
        const response = await fetch(`${API_BASE_URL}/patients/?search=${encodeURIComponent(searchTerm)}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.warn(`❌ Search failed for "${searchTerm}": ${response.status}`);
          continue;
        }

        const data = await response.json();
        console.log(`📋 Search results for "${searchTerm}":`, data);
        
        if (data.patients && data.patients.length > 0) {
          foundPatients = [...foundPatients, ...data.patients];
          console.log(`✅ Found ${data.patients.length} patients with search term "${searchTerm}"`);
        }
      } catch (error) {
        console.warn(`❌ Error searching for "${searchTerm}":`, error);
      }
    }

    // Remove duplicates based on patient ID
    const uniquePatients = foundPatients.filter((patient, index, self) =>
      index === self.findIndex(p => p.id === patient.id)
    );

    console.log(`📊 Total unique patients found: ${uniquePatients.length}`);

    // Filter by DOB if provided
    if (dob && uniquePatients.length > 0) {
      console.log(`🔍 Filtering by DOB: ${dob}`);
      
      const matchingPatients = uniquePatients.filter(patient => {
        const patientDOB = patient.dob ? new Date(patient.dob).toISOString().split('T')[0] : null;
        console.log(`📅 Comparing patient DOB: ${patientDOB} with search DOB: ${dob}`);
        return patientDOB === dob;
      });
      
      console.log(`📅 Patients matching DOB: ${matchingPatients.length}`);
      
      if (matchingPatients.length === 0) {
        throw new Error(`Found ${uniquePatients.length} patient(s) with similar names, but none match the date of birth ${dob}`);
      }
      
      // Return the first matching patient
      console.log('✅ Returning patient with matching DOB:', matchingPatients[0]);
      return matchingPatients[0];
    }
    
    // If no DOB provided, return first patient if found
    if (uniquePatients.length > 0) {
      console.log('✅ Returning first patient found:', uniquePatients[0]);
      return uniquePatients[0];
    }
    
    throw new Error(`No patient found with name "${firstName} ${lastName}"`);
  },

  // Get all specialties (from doctors, not clinicians)
  getSpecialties: async () => {
    const token = localStorage.getItem('authToken');
    
    try {
      // Try doctor specialties first (like patient module)
      const doctorResponse = await fetch(`${API_BASE_URL}/doctors/specialties`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (doctorResponse.ok) {
        return await doctorResponse.json();
      }
    } catch (error) {
      console.warn('Could not fetch doctor specialties:', error);
    }

    // Fallback: try to get unique specialties from all doctors
    try {
      const doctorsResponse = await fetch(`${API_BASE_URL}/doctors/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (doctorsResponse.ok) {
        const doctors = await doctorsResponse.json();
        const specialtiesSet = new Set();
        
        doctors.forEach(doctor => {
          if (doctor.specialties) {
            // Split specialties if they're comma-separated
            const doctorSpecialties = doctor.specialties.split(',').map(s => s.trim());
            doctorSpecialties.forEach(specialty => {
              if (specialty) {
                specialtiesSet.add(specialty);
              }
            });
          }
        });
        
        const uniqueSpecialties = Array.from(specialtiesSet).sort();
        if (uniqueSpecialties.length > 0) {
          return uniqueSpecialties;
        }
      }
    } catch (error) {
      console.warn('Could not fetch doctors for specialties:', error);
    }

    // Final fallback to mock specialties if all APIs fail
    return [
      'General Practice',
      'Cardiology', 
      'Dermatology',
      'Endocrinology',
      'Gastroenterology',
      'Neurology',
      'Orthopedics',
      'Pediatrics',
      'Psychiatry',
      'Radiology'
    ];
  },

  // Get doctors by specialty
  getDoctorsBySpecialty: async (specialty) => {
    const token = localStorage.getItem('authToken');
    
    try {
      // Use the correct doctor specialty endpoint
      const response = await fetch(`${API_BASE_URL}/doctors/by-specialty/${encodeURIComponent(specialty)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.warn('Could not fetch doctors by specialty from dedicated endpoint:', error);
    }

    // Fallback: try search endpoint (correct parameter name)
    try {
      const response = await fetch(`${API_BASE_URL}/doctors/search?search_term=${encodeURIComponent(specialty)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const doctors = await response.json();
        // Filter results to match the specialty more precisely
        return doctors.filter(doctor => 
          doctor.specialties && doctor.specialties.toLowerCase().includes(specialty.toLowerCase())
        );
      }
    } catch (error) {
      console.warn('Could not search doctors by specialty:', error);
    }

    // Final fallback: get all doctors and filter by specialty
    try {
      const response = await fetch(`${API_BASE_URL}/doctors/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const doctors = await response.json();
        return doctors.filter(doctor => 
          doctor.specialties && doctor.specialties.toLowerCase().includes(specialty.toLowerCase())
        );
      }
    } catch (error) {
      console.warn('Could not fetch doctors:', error);
    }

    // Return empty array if all fails
    return [];
  },

  // Get clinics by doctor
  getClinicsByDoctor: async (doctorId) => {
    const token = localStorage.getItem('authToken');
    
    console.log(`🏥 Fetching clinics for doctor ${doctorId}`);
    
    try {
      // Use the correct doctor-clinic endpoint
      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/clinics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const clinics = await response.json();
        console.log(`✅ Found ${clinics.length} clinics for doctor ${doctorId}:`, clinics);
        return clinics;
      } else {
        console.warn(`⚠️ Doctor clinics API error: ${response.status}`);
      }
    } catch (error) {
      console.warn('⚠️ Could not fetch doctor clinics:', error);
    }

    // Fallback: return mock clinic data
    console.log('📝 Using fallback clinic data');
    return [
      { 
        clinic_id: '1', 
        clinic_name: 'Main Medical Center', 
        clinic_address: '123 Health St, City', 
        suburb: 'Central',
        state: 'NSW',
        is_primary_clinic: true 
      },
      { 
        clinic_id: '2', 
        clinic_name: 'Downtown Clinic', 
        clinic_address: '456 Medical Ave, City',
        suburb: 'Downtown', 
        state: 'NSW',
        is_primary_clinic: false 
      }
    ];
  },

  // Get doctor availability for a specific date (real-time slots)
  getDoctorDaySlots: async (doctorId, isoDate) => {
    const token = localStorage.getItem('authToken');
    
    console.log(`🕐 Fetching day slots for doctor ${doctorId} on ${isoDate}`);
    
    try {
      // Use the patient module's approach - real availability API
      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/availability/day/${isoDate}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const slots = await response.json();
        console.log(`✅ Got ${slots.length} slots from availability API:`, slots);
        
        // Extract available time slots like patient module
        const availableTimes = (slots || [])
          .filter((s) => s.status === 'AVAILABLE')
          .map((s) => s.start_time.slice(0, 5)); // Format HH:MM
        
        console.log(`🕐 Available times: ${availableTimes.length} slots:`, availableTimes);
        return availableTimes;
      } else {
        console.warn(`⚠️ Day slots API error: ${response.status}`);
      }
    } catch (error) {
      console.warn('⚠️ Could not fetch day slots from availability API:', error);
    }

    // Fallback: Check existing bookings and generate realistic times
    try {
      const bookingsResponse = await fetch(`${API_BASE_URL}/patient-bookings/all?appointment_date=${isoDate}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      let bookedTimes = [];
      if (bookingsResponse.ok) {
        const bookings = await bookingsResponse.json();
        const doctorBookings = Array.isArray(bookings) 
          ? bookings.filter(booking => booking.doctor_id == doctorId)
          : [];
        
        bookedTimes = doctorBookings.map(booking => booking.appointment_time);
        console.log(`📅 Found ${doctorBookings.length} existing bookings:`, bookedTimes);
      }

      // Generate realistic available times (excluding booked ones)
      const allTimes = [
        '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
        '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
        '17:00', '17:30'
      ];

      const availableTimes = allTimes.filter(time => !bookedTimes.includes(time));
      console.log(`🕐 Fallback generated ${availableTimes.length} available times`);
      return availableTimes.length > 0 ? availableTimes : ['14:00', '15:00', '16:00'];

    } catch (error) {
      console.warn('⚠️ Could not check existing bookings:', error);
    }

    // Final fallback: return standard available times
    console.log('📝 Using default available times');
    return [
      '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
      '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
    ];
  },

  // Get doctor's month availability (for calendar color coding)
  getDoctorMonthAvailability: async (doctorId, year, month) => {
    const token = localStorage.getItem('authToken');
    
    console.log(`🗓️ Fetching month availability for doctor ${doctorId}, ${year}-${month}`);
    
    try {
      // Use the patient module's approach - real availability API
      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/availability/month?year=${year}&month=${month}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const availability = await response.json();
        console.log(`✅ Got month availability from API:`, availability);
        return availability; // { doctor_id, year, month, days: [{ avail_date, is_available, available_slots_count }] }
      } else {
        console.warn(`⚠️ Month availability API error: ${response.status}`);
        if (response.status === 403) {
          console.warn('⚠️ Admin may not have permission - trying fallback approach');
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not fetch month availability from API:', error);
    }

    // Smart fallback: Generate realistic availability based on existing bookings
    try {
      console.log('📝 Using smart fallback for month availability');
      
      // Generate days for the month
      const daysInMonth = new Date(year, month, 0).getDate();
      const days = [];
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month - 1, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        date.setHours(0, 0, 0, 0);
        
        // Only show availability for future dates
        if (date >= today) {
          const isoDate = date.toISOString().split('T')[0];
          
          // Simulate realistic availability (70% chance of having slots)
          const isAvailable = Math.random() > 0.3;
          const availableSlots = isAvailable ? Math.floor(Math.random() * 8) + 2 : 0; // 2-10 slots
          
          days.push({
            avail_date: isoDate,
            is_available: isAvailable,
            available_slots_count: availableSlots
          });
        }
      }
      
      console.log(`🗓️ Generated fallback availability for ${days.length} days`);
      return {
        doctor_id: doctorId,
        year: year,
        month: month,
        days: days
      };
    } catch (error) {
      console.warn('⚠️ Could not generate fallback availability:', error);
    }

    // Final fallback: return null (no availability data)
    console.log('📝 No availability data available');
    return null;
  },

  // Create booking for patient
  createBookingForPatient: async (bookingData) => {
    const token = localStorage.getItem('authToken');
    
    console.log('📝 Original booking data:', bookingData);
    
    // Validate required integer fields
    if (!Number.isInteger(bookingData.user_id) || bookingData.user_id <= 0) {
      throw new Error(`Invalid user_id: ${bookingData.user_id}. Must be a positive integer.`);
    }
    
    if (!Number.isInteger(bookingData.patient_id) || bookingData.patient_id <= 0) {
      throw new Error(`Invalid patient_id: ${bookingData.patient_id}. Must be a positive integer.`);
    }
    
    // Format the data properly for backend validation
    const formattedData = {
      ...bookingData,
      // Ensure appointment_date is in YYYY-MM-DD format
      appointment_date: bookingData.appointment_date,
      // Ensure appointment_time is in HH:MM:SS format  
      appointment_time: bookingData.appointment_time.includes(':') 
        ? (bookingData.appointment_time.split(':').length === 2 
            ? `${bookingData.appointment_time}:00` 
            : bookingData.appointment_time)
        : `${bookingData.appointment_time}:00:00`,
      // Ensure doctor_id is integer or null
      doctor_id: bookingData.doctor_id && Number.isInteger(bookingData.doctor_id) ? bookingData.doctor_id : null,
      // user_id and patient_id are already validated as integers above
      user_id: bookingData.user_id,
      patient_id: bookingData.patient_id
    };
    
    console.log('📤 Formatted booking data being sent:', formattedData);
    
    const response = await fetch(`${API_BASE_URL}/patient-bookings/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formattedData),
    });

    console.log('📥 Response status:', response.status);
    console.log('📥 Response ok:', response.ok);

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      let errorDetails = null;
      
      try {
        const errorData = await response.json();
        console.error('❌ Detailed error response:', errorData);
        errorDetails = errorData;
        errorMessage = errorData.detail || errorData.message || errorMessage;
        
        // Handle validation errors specifically
        if (Array.isArray(errorData.detail)) {
          const validationErrors = errorData.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        }
      } catch (parseError) {
        console.error('❌ Could not parse error response:', parseError);
      }
      
      console.error('❌ Final error message:', errorMessage);
      console.error('❌ Error details:', errorDetails);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('✅ Booking created successfully:', result);
    return result;
  },

  // Get patient by user_id (for linking user to patient record)
  getPatientByUserId: async (userId) => {
    const token = localStorage.getItem('authToken');
    
    try {
      const response = await fetch(`${API_BASE_URL}/patients/by-user/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.warn('Could not fetch patient by user ID:', error);
    }

    return null;
  },

  // Get all clinics
  getAllClinics: async () => {
    const token = localStorage.getItem('authToken');
    
    try {
      const response = await fetch(`${API_BASE_URL}/clinics/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching all clinics:', error);
      throw error;
    }
  },
};

export default adminBookingService;
